import { Dispatch, useEffect, useRef, useState } from 'react';

import { useQuery } from '@apollo/client';
import ReportJobByJobIdQuery, {
  ReportJobByJobIdResponse,
} from '@graphql/queries/ReportJobByJobIdQuery';

import { ProductCatalog } from './useProductCatalog';
import { ReportRequestAction } from '../model/reducer';
import {
  resolveTemplateProducts,
  stateFromTemplate,
  TemplateFollowUp,
} from '../model/templates';

export type Prefill = ReturnType<typeof stateFromTemplate> | null;

export interface TemplatePrefillStatus {
  /** Product names from the template that are no longer in the catalog. */
  missingProducts: string[];
  /** True while products or the parent report are still being resolved. */
  resolving: boolean;
  /** The template's parent report could not be found. */
  parentMissing: boolean;
}

/**
 * Completes a Clone / Update prefill. The initial state comes from
 * `stateFromTemplate`; fields that only exist as names in the params
 * (products, the parent report) are resolved here once their lookups load.
 */
const useTemplatePrefill = (
  prefill: Prefill,
  catalog: ProductCatalog,
  dispatch: Dispatch<ReportRequestAction>,
): TemplatePrefillStatus => {
  const followUp: TemplateFollowUp | null = prefill?.followUp ?? null;
  const [missingProducts, setMissingProducts] = useState<string[]>([]);
  const productsDone = useRef(!followUp || followUp.productNames.length === 0);
  const parentDone = useRef(!followUp?.parentJobId);

  useEffect(() => {
    if (productsDone.current || catalog.loading || catalog.catalog.length === 0) return;
    productsDone.current = true;
    const { rows, missing } = resolveTemplateProducts(
      followUp.productNames,
      catalog.byName,
      prefill.state.allowedWorkspaces,
      followUp.productsAreOriginal,
    );
    dispatch({ type: 'addProducts', products: rows });
    // addProducts auto-allows workspaces and categories; the template's choices win.
    if (prefill.state.allowedWorkspaces.length) {
      dispatch({ type: 'set', key: 'allowedWorkspaces', value: prefill.state.allowedWorkspaces });
    }
    dispatch({ type: 'set', key: 'categories', value: prefill.state.categories });
    setMissingProducts(missing);
  }, [catalog, followUp, prefill, dispatch]);

  const parentQuery = useQuery<ReportJobByJobIdResponse>(ReportJobByJobIdQuery, {
    skip: parentDone.current,
    variables: { jobId: followUp?.parentJobId },
  });
  const parentNode = parentQuery.data?.allReportJobs.nodes[0];

  useEffect(() => {
    if (parentDone.current || parentQuery.loading || !parentQuery.data) return;
    parentDone.current = true;
    if (!parentNode) return;
    dispatch({
      type: 'restoreParent',
      parent: {
        reportId: parentNode.reportId,
        jobId: parentNode.jobId,
        startedAt: parentNode.startedAt,
        clientName: parentNode.clientName ?? '',
        projectName: parentNode.projectName ?? '',
        targetGroupName: parentNode.targetGroupName ?? '',
        workspaceId: parentNode.workspaceId,
        passedQa: !!parentNode.passedQa,
      },
      optimization: followUp.optimization,
      mixAndMatch: followUp.mixAndMatch.map((m) => ({ ...m, label: m.reportId })),
    });
  }, [parentQuery.loading, parentQuery.data, parentNode, followUp, dispatch]);

  return {
    missingProducts,
    resolving:
      (!productsDone.current && catalog.loading) ||
      (!parentDone.current && parentQuery.loading),
    parentMissing:
      !!followUp?.parentJobId && !!parentQuery.data && !parentNode,
  };
};

export default useTemplatePrefill;
